import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

/** Field-app runtime cache holds per-user API responses; never let it outlive a session. */
function clearFieldCache() {
  if (typeof caches !== 'undefined') caches.delete('field-data').catch(() => {});
}

function clearSession() {
  clearFieldCache();
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch((err) => {
        // Only a real rejection (401) ends the session; offline / network errors keep
        // the cached user so the field app still opens without signal.
        if (err?.status === 401) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    clearFieldCache();
    const { user: u, token } = await api.login({ email, password });
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  // Contractor self-sign-up (Part 8) — the only public sign-up; signs the new contractor in.
  const signUpContractor = useCallback(async (body) => {
    clearFieldCache();
    const { user: u, token } = await api.contractorSignup(body);
    persistSession(u, token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    // Offline API caches (service worker, keyed by URL) must not show this user's data to the next one.
    if (typeof caches !== 'undefined') caches.delete('field-data').catch(() => {});
    setUser(null);
  }, []);

  const isAdmin = !!(user?.role === 'Administrator' || user?.is_admin_role || user?.isAdminRole);
  const value = { user, loading, isAuthenticated: !!user, isAdmin, login, signUpContractor, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
